import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import { useState } from "react";
import {
  postV2UiPreferenceSwitchbackFeedback,
  V2FeedbackCategory,
} from "../../../api/feedback";

interface Props {
  open: boolean;
  customerId: string;
  fromUrl: string;
  onSwitch: () => void;
  onClose: () => void;
}

const CATEGORIES: { value: V2FeedbackCategory; label: string }[] = [
  { value: "missing_feature", label: "Missing feature" },
  { value: "performance", label: "Performance issue" },
  { value: "visual_bug", label: "Visual bug" },
  { value: "preference", label: "Just preference" },
  { value: "other", label: "Other" },
];

export function SwitchToV1FeedbackDialog({
  open,
  customerId,
  fromUrl,
  onSwitch,
  onClose,
}: Props) {
  const [category, setCategory] = useState<V2FeedbackCategory | "">("");
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!category) return;
    setSubmitting(true);
    try {
      await postV2UiPreferenceSwitchbackFeedback({
        customerId,
        category,
        comment: comment.trim() ? comment.trim() : undefined,
        fromUrl,
      });
    } catch {
      // Fire-and-forget: user must not be blocked if POST fails.
    }
    setSubmitting(false);
    onSwitch();
  };

  const handleSkip = () => {
    onSwitch();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          backgroundColor: "var(--bg-surface)",
          color: "var(--text-primary)",
          border: "1px solid var(--border-mid)",
          borderRadius: "8px",
        },
      }}
    >
      <DialogTitle sx={{ color: "var(--text-primary)", fontFamily: "'DM Sans', -apple-system, sans-serif" }}>
        What's making you switch back?
      </DialogTitle>
      <DialogContent>
        <RadioGroup
          value={category}
          onChange={(e) => setCategory(e.target.value as V2FeedbackCategory)}
        >
          {CATEGORIES.map((c) => (
            <FormControlLabel
              key={c.value}
              value={c.value}
              control={
                <Radio
                  sx={{
                    color: "var(--text-tertiary)",
                    "&.Mui-checked": { color: "var(--accent)" },
                  }}
                />
              }
              label={c.label}
              sx={{ "& .MuiFormControlLabel-label": { color: "var(--text-secondary)" } }}
            />
          ))}
        </RadioGroup>
        <TextField
          fullWidth
          multiline
          minRows={3}
          maxRows={6}
          inputProps={{ maxLength: 2000 }}
          placeholder="Anything else you'd like us to know? (optional)"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          sx={{
            mt: 2,
            "& .MuiOutlinedInput-root": {
              color: "var(--text-primary)",
              "& fieldset": { borderColor: "var(--border-mid)" },
              "&:hover fieldset": { borderColor: "var(--border-strong)" },
              "&.Mui-focused fieldset": { borderColor: "var(--accent)" },
            },
            "& .MuiOutlinedInput-input::placeholder": {
              color: "var(--text-muted)",
              opacity: 1,
            },
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleSkip}
          disabled={submitting}
          sx={{ color: "var(--accent)", "&.Mui-disabled": { color: "var(--text-muted)" } }}
        >
          Skip & just switch
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!category || submitting}
          sx={{
            backgroundColor: "var(--accent)",
            color: "#FFFFFF",
            "&:hover": { backgroundColor: "var(--accent)", opacity: 0.9 },
            "&.Mui-disabled": {
              backgroundColor: "var(--accent)",
              color: "#FFFFFF",
              opacity: 0.4,
            },
          }}
        >
          Switch & send feedback
        </Button>
      </DialogActions>
    </Dialog>
  );
}
